import { ApiError, request, type BookingStatus, type Location } from '../api';

/** Verwaltungs-Schnittstelle. Sitzung per Cookie, Änderungen mit dem Schutz-Header gegen CSRF. */

export interface Identity {
  user: { email: string; name: string | null };
  access?: { via: 'session' | 'api_key'; scopes: string[] | 'all' };
}

export interface ScopeInfo {
  scope: string;
  title: string;
  description: string;
}

export interface ApiKeyRow {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
}

export interface ConnectionRow {
  clientId: string;
  clientName: string | null;
  scopes: string[];
  connectedAt: string;
  lastUsedAt: string | null;
  active: boolean;
}

export interface AuditEntry {
  actorKind: 'login' | 'api_key';
  actorLabel: string | null;
  method: string;
  path: string;
  status: number;
  summary: string;
  at: string;
}

export interface WebhookEndpoint {
  url: string;
  active: boolean;
  createdAt: string;
}

export interface WebhookDelivery {
  id: string;
  eventType: 'booking.created' | 'booking.rescheduled' | 'booking.cancelled' | 'booking.declined' | 'ping';
  bookingId: string | null;
  status: 'queued' | 'delivered' | 'failed';
  attempts: number;
  nextAttemptAt: string | null;
  lastStatusCode: number | null;
  lastError: string | null;
  createdAt: string;
  deliveredAt: string | null;
}

export interface EventType {
  id: string;
  brandId: string;
  slug: string;
  title: string;
  description: string | null;
  durationMinutes: number;
  location: Location;
  active: boolean;
  leadTimeHours: number;
  horizonDays: number;
  /** E-Mail-Code vor der Buchung (Standard an). */
  requiresEmailVerification: boolean;
  /** Bestätigung durch den Nutzer (Standard aus). */
  requiresHostConfirmation: boolean;
  createdAt: string;
}

export interface Brand {
  id: string;
  slug: string;
  name: string;
  senderName: string | null;
  brandColor: string | null;
  logoUrl: string | null;
  imprintUrl: string | null;
  privacyUrl: string | null;
  createdAt: string;
  eventTypes: EventType[];
}

export interface BrandPatch {
  name?: string;
  slug?: string;
  senderName?: string | null;
  brandColor?: string | null;
  imprintUrl?: string | null;
  privacyUrl?: string | null;
}

export interface EventTypePatch {
  title?: string;
  slug?: string;
  description?: string | null;
  durationMinutes?: number;
  location?: Location;
  active?: boolean;
  leadTimeHours?: number;
  horizonDays?: number;
  requiresEmailVerification?: boolean;
  requiresHostConfirmation?: boolean;
}

export interface Rule {
  weekday: number;
  start: string;
  end: string;
}

export interface Override {
  id: string;
  day: string;
  kind: 'open' | 'blocked';
  eventTypeId: string | null;
  eventTypeTitle: string | null;
  start: string | null;
  end: string | null;
  note: string | null;
}

export interface OverrideInput {
  day: string;
  kind: 'open' | 'blocked';
  eventTypeId?: string | null;
  start?: string | null;
  end?: string | null;
  note?: string | null;
}

export interface Booking {
  id: string;
  startsAt: string;
  endsAt: string;
  /** pending = Anfrage, declined = abgelehnt, expired = ohne Antwort verstrichen. */
  status: BookingStatus;
  name: string;
  email: string;
  phone: string | null;
  company: string | null;
  message: string | null;
  eventType: { id: string; title: string; location: Location };
  brand: { id: string; name: string; slug: string };
  cancelledAt: string | null;
  cancelledBy: 'customer' | 'host' | null;
  cancelReason: string | null;
  createdAt: string;
  emailVerifiedAt: string | null;
  /** Google-Kalender (Konzept 5): Meet-Link und Stand des Google-Termins; null ohne Google-Verbindung. */
  meetUrl: string | null;
  googleSyncStatus: 'pending' | 'synced' | 'failed' | null;
  googleSyncError: string | null;
}

/** Terminwunsch (Konzept 5.4). */
export interface BookingRequest {
  id: string;
  name: string;
  email: string;
  status: 'open' | 'notified' | 'closed';
  eventType: { id: string; title: string; slug: string };
  brand: { id: string; name: string; slug: string };
  createdAt: string;
  notifiedAt: string | null;
}

/** Google-Kalender (Konzept 5). */
export interface GoogleCalendarRow {
  id: string;
  summary: string;
  primary: boolean;
  busy: boolean;
  target: boolean;
}

export interface GoogleConnection {
  email: string;
  status: 'ok' | 'needs_reauth' | 'error';
  lastError: string | null;
  lastCheckedAt: string | null;
  connectedAt: string;
  targetCalendarId: string;
  busyCalendarIds: string[];
  calendars: GoogleCalendarRow[];
  calendarsStale: boolean;
  pendingJobs: number;
}

export interface GoogleState {
  configured: boolean;
  connection: GoogleConnection | null;
}

const call = <T>(path: string, init: { method?: string; body?: unknown } = {}) => request<T>(`/api/admin${path}`, { ...init, admin: true });

async function upload<T>(path: string, file: File): Promise<T> {
  const res = await fetch(`/api/admin${path}`, {
    method: 'PUT',
    credentials: 'same-origin',
    headers: { Accept: 'application/json', 'X-Requested-With': 'fetch', 'Content-Type': file.type || 'image/jpeg' },
    body: file,
  });
  if (!res.ok) {
    let message = '';
    try {
      message = ((await res.json()) as { message?: string }).message ?? '';
    } catch {
      /* kein JSON */
    }
    throw new ApiError(res.status, message || (res.status === 413 ? 'Das Logo ist größer als 2 MB.' : 'Bitte ein JPG, PNG oder WebP hochladen.'));
  }
  return (await res.json()) as T;
}

export const adminApi = {
  me: () => call<Identity>('/me'),
  login: (email: string, password: string) => call<Identity>('/login', { method: 'POST', body: { email, password } }),
  register: (email: string, password: string, name: string) => call<Identity>('/register', { method: 'POST', body: { email, password, name } }),
  logout: () => call<{ ok: true }>('/logout', { method: 'POST' }),
  requestPasswordReset: (email: string) => call<{ ok: true }>('/password-reset', { method: 'POST', body: { email } }),
  resetPassword: (token: string, password: string) => call<Identity>('/password-reset/confirm', { method: 'POST', body: { token, password } }),
  changePassword: (currentPassword: string, newPassword: string) => call<{ ok: true }>('/password', { method: 'POST', body: { currentPassword, newPassword } }),
  listBrands: () => call<{ brands: Brand[] }>('/brands'),
  createBrand: (name: string) => call<{ id: string }>('/brands', { method: 'POST', body: { name } }),
  getBrand: (id: string) => call<Brand>(`/brands/${id}`),
  updateBrand: (id: string, patch: BrandPatch) => call<Brand>(`/brands/${id}`, { method: 'PATCH', body: patch }),
  deleteBrand: (id: string) => call<{ ok: true }>(`/brands/${id}`, { method: 'DELETE' }),
  uploadLogo: (id: string, file: File) => upload<{ logoUrl: string }>(`/brands/${id}/logo`, file),
  removeLogo: (id: string) => call<{ logoUrl: null }>(`/brands/${id}/logo`, { method: 'DELETE' }),
  createEventType: (brandId: string, title: string, durationMinutes: number) => call<{ id: string }>(`/brands/${brandId}/event-types`, { method: 'POST', body: { title, durationMinutes } }),
  updateEventType: (id: string, patch: EventTypePatch) => call<EventType>(`/event-types/${id}`, { method: 'PATCH', body: patch }),
  deleteEventType: (id: string) => call<{ ok: true }>(`/event-types/${id}`, { method: 'DELETE' }),
  listRules: (eventTypeId: string) => call<{ rules: (Rule & { id: string })[] }>(`/event-types/${eventTypeId}/rules`),
  putRules: (eventTypeId: string, rules: Rule[]) => call<{ rules: (Rule & { id: string })[] }>(`/event-types/${eventTypeId}/rules`, { method: 'PUT', body: { rules } }),
  listOverrides: () => call<{ overrides: Override[] }>('/overrides'),
  createOverride: (input: OverrideInput) => call<Override>('/overrides', { method: 'POST', body: input }),
  deleteOverride: (id: string) => call<{ ok: true }>(`/overrides/${id}`, { method: 'DELETE' }),
  listBookings: (scope: 'upcoming' | 'past' | 'pending') => call<{ bookings: Booking[] }>(`/bookings?scope=${scope}`),
  cancelBooking: (id: string, reason: string) => call<Booking>(`/bookings/${id}/cancel`, { method: 'POST', body: { reason } }),
  // Anfragen (Bestätigung durch den Nutzer)
  acceptBooking: (id: string) => call<Booking>(`/bookings/${id}/accept`, { method: 'POST', body: {} }),
  declineBooking: (id: string, reason: string) => call<Booking>(`/bookings/${id}/decline`, { method: 'POST', body: { reason } }),
  // Terminwünsche (Konzept 5.4)
  listBookingRequests: () => call<{ requests: BookingRequest[] }>('/booking-requests'),
  closeBookingRequest: (id: string) => call<BookingRequest>(`/booking-requests/${id}/close`, { method: 'POST' }),
  // Google-Kalender (Konzept 5)
  getGoogle: () => call<GoogleState>('/google'),
  saveGoogle: (patch: { busyCalendarIds?: string[]; targetCalendarId?: string }) => call<GoogleConnection>('/google', { method: 'PUT', body: patch }),
  disconnectGoogle: () => call<{ ok: true }>('/google', { method: 'DELETE' }),
  retryGoogle: () => call<{ done: number; retried: number; failed: number; notified: number; view: GoogleState }>('/google/retry', { method: 'POST' }),
  // KI verbinden (Konzept 9.2)
  listApiKeys: () => call<{ keys: ApiKeyRow[]; scopes: ScopeInfo[] }>('/api-keys'),
  createApiKey: (name: string, scopes: string[], expiresInDays: number | null) => call<{ key: string; row: ApiKeyRow }>('/api-keys', { method: 'POST', body: { name, scopes, expiresInDays } }),
  revokeApiKey: (id: string) => call<{ ok: true }>(`/api-keys/${id}`, { method: 'DELETE' }),
  listConnections: () => call<{ connections: ConnectionRow[]; scopes: ScopeInfo[] }>('/connections'),
  disconnect: (clientId: string) => call<{ ok: true }>(`/connections/${clientId}`, { method: 'DELETE' }),
  auditLog: (limit = 50) => call<{ entries: AuditEntry[] }>(`/audit-log?limit=${limit}`),
  // Anbindung an das Erfolgsmagneten OS (Konzept 9.1)
  getWebhook: () => call<{ endpoint: WebhookEndpoint | null; deliveries: WebhookDelivery[] }>('/webhook'),
  saveWebhook: (patch: { url?: string; active?: boolean }) => call<{ endpoint: WebhookEndpoint; secret: string | null }>('/webhook', { method: 'PUT', body: patch }),
  rotateWebhookSecret: () => call<{ secret: string }>('/webhook/secret', { method: 'POST' }),
  testWebhook: () => call<{ delivery: WebhookDelivery | null }>('/webhook/test', { method: 'POST' }),
};
